/**
 * Author: Claude Code
 * Version: v3.0.78
 * Modified: 2025-09-08 Beijing Time
 * 
 * AgentSystem - 前端Agent可视化管理系统
 * 桥接后端AgentManager，处理Agent的视觉呈现和动画
 */

import Phaser from 'phaser';
import { MovementAnimationSync } from '../animation/MovementAnimationSync';

export interface AgentData {
  id: string;
  position: { x: number; y: number };
  health: number;
  maxHealth: number;
  color: string;
}

export interface AgentSprite extends Phaser.GameObjects.Sprite {
  agentData: AgentData;
}

export class AgentSystem {
  private scene: Phaser.Scene;
  private agents: Map<string, AgentSprite> = new Map();
  private agentGroup: Phaser.GameObjects.Group;
  private movementSync: MovementAnimationSync;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.agentGroup = scene.add.group();
    // 初始化移动动画同步器
    this.movementSync = new MovementAnimationSync(scene);
  }

  // Agent spawn management
  spawnAgent(agentData: AgentData): void {
    const sprite = this.scene.add.sprite(
      agentData.position.x,
      agentData.position.y,
      `slime-${agentData.color}-idle`
    ) as AgentSprite;

    sprite.setDepth(10);
    sprite.setOrigin(0.5, 0.5);
    sprite.setDisplaySize(24, 24);
    
    // Store agent data with sprite
    sprite.agentData = { ...agentData };
    
    this.agents.set(agentData.id, sprite);
    this.agentGroup.add(sprite);
  }

  removeAgent(agentId: string): void {
    const sprite = this.agents.get(agentId);
    if (sprite) {
      this.agentGroup.remove(sprite);
      sprite.destroy();
      this.agents.delete(agentId);
    }
  }

  getAgent(agentId: string): AgentData | undefined {
    const sprite = this.agents.get(agentId);
    return sprite?.agentData;
  }

  // Position management
  updateAgentPosition(agentId: string, position: { x: number; y: number }): void {
    const sprite = this.agents.get(agentId);
    if (sprite) {
      sprite.setPosition(position.x, position.y);
      sprite.agentData.position = position;
    }
  }

  // 🎮 增强：使用 MovementAnimationSync 的tile跳跃移动
  moveAgentToTile(agentId: string, fromTile: { x: number; y: number }, toTile: { x: number; y: number }, duration: number = 300): Promise<void> {
    const sprite = this.agents.get(agentId);
    if (!sprite) {
      return Promise.reject(new Error(`Agent not found: ${agentId}`));
    }

    const TILE_SIZE = 48;
    // 统一使用下划线动画键，移动动画为 mov
    const walkAnimKey = `${sprite.agentData.color}_mov`;
    const idleAnimKey = `${sprite.agentData.color}_idle`;

    return new Promise<void>((resolve, reject) => {
      // 使用增强的MovementAnimationSync处理移动动画同步
      const success = this.movementSync.performSynchronizedMove(
        sprite,
        agentId,
        toTile.x,
        toTile.y,
        TILE_SIZE,
        walkAnimKey,
        idleAnimKey,
        () => {
          // 更新agentData中的位置
          sprite.agentData.position = { 
            x: toTile.x * TILE_SIZE + TILE_SIZE / 2, 
            y: toTile.y * TILE_SIZE + TILE_SIZE / 2 
          };
          console.log(`🎯 AgentSystem: ${agentId} 移动完成到tile (${toTile.x}, ${toTile.y})`);
          resolve();
        }
      );

      if (!success) {
        reject(new Error(`Movement failed for agent ${agentId} - already moving`));
      }
    });
  }

  // 🥊 新增：计算相邻位置 (上下左右)
  private calculateAdjacentPosition(defenderTile: { x: number; y: number }, attackerTile: { x: number; y: number }): { x: number; y: number } {
    const dx = attackerTile.x - defenderTile.x;
    const dy = attackerTile.y - defenderTile.y;
    
    // 找到最近的相邻位置 (上下左右优先)
    if (Math.abs(dx) > Math.abs(dy)) {
      // 水平相邻
      return { x: defenderTile.x + (dx > 0 ? 1 : -1), y: defenderTile.y };
    } else {
      // 垂直相邻  
      return { x: defenderTile.x, y: defenderTile.y + (dy > 0 ? 1 : -1) };
    }
  }

  // 🥊 新增：发起战斗 - A移动到B旁边，面对面攻击
  async initiateCombat(attackerId: string, defenderId: string): Promise<void> {
    const attackerSprite = this.agents.get(attackerId);
    const defenderSprite = this.agents.get(defenderId);
    
    if (!attackerSprite || !defenderSprite) {
      console.warn(`Combat failed: missing agents ${attackerId} or ${defenderId}`);
      return;
    }

    // 计算当前tile位置
    const TILE_SIZE = 48;
    const defenderTile = {
      x: Math.floor(defenderSprite.x / TILE_SIZE),
      y: Math.floor(defenderSprite.y / TILE_SIZE)
    };
    const attackerTile = {
      x: Math.floor(attackerSprite.x / TILE_SIZE),
      y: Math.floor(attackerSprite.y / TILE_SIZE)
    };

    // 计算攻击者应该移动到的相邻位置
    const adjacentPos = this.calculateAdjacentPosition(defenderTile, attackerTile);
    
    // 1. 攻击者移动到防守者旁边
    await this.moveAgentToTile(attackerId, attackerTile, adjacentPos);
    
    // 2. 设置面对面方向
    const attackerFinalSprite = this.agents.get(attackerId)!;
    const defenderFinalSprite = this.agents.get(defenderId)!;
    
    // 攻击者面对防守者
    attackerFinalSprite.setFlipX(attackerFinalSprite.x > defenderFinalSprite.x);
    // 防守者面对攻击者
    defenderFinalSprite.setFlipX(defenderFinalSprite.x > attackerFinalSprite.x);
    
    console.log(`Combat initiated: ${attackerId} vs ${defenderId} - face to face setup completed`);
  }

  // 🥊 新增：执行战斗序列 - 轮流攻击动画
  async executeCombatSequence(attackerId: string, defenderId: string, rounds: number = 1): Promise<void> {
    for (let round = 0; round < rounds; round++) {
      // 攻击者攻击
      await this.performAttack(attackerId, defenderId);
      
      // 等待一点时间
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // 防守者反击
      await this.performAttack(defenderId, attackerId);
      
      // 等待下一轮
      if (round < rounds - 1) {
        await new Promise(resolve => setTimeout(resolve, 300));
      }
    }
  }

  // 🥊 辅助方法：执行单次攻击动画
  private performAttack(attackerId: string, defenderId: string): Promise<void> {
    const attackerSprite = this.agents.get(attackerId);
    const defenderSprite = this.agents.get(defenderId);
    
    if (!attackerSprite || !defenderSprite) {
      return Promise.resolve();
    }

    return new Promise<void>((resolve) => {
      // 攻击者播放atk动画
      const attackAnimKey = `${attackerSprite.agentData.color}-atk`;
      attackerSprite.play(attackAnimKey);
      
      // 防守者播放hurt动画
      const hurtAnimKey = `${defenderSprite.agentData.color}-hurt`;  
      defenderSprite.play(hurtAnimKey);

      // 等待攻击动画完成 (按帧率计算: 8帧 * (1000/15fps) = ~533ms)
      setTimeout(() => {
        // 双方回到idle
        const attackerIdleKey = `${attackerSprite.agentData.color}-idle`;
        const defenderIdleKey = `${defenderSprite.agentData.color}-idle`;
        
        attackerSprite.play(attackerIdleKey);
        defenderSprite.play(defenderIdleKey);
        
        resolve();
      }, 600);
    });
  }

  batchUpdatePositions(updates: Array<{ id: string; position: { x: number; y: number } }>): void {
    updates.forEach(update => {
      this.updateAgentPosition(update.id, update.position);
    });
  }

  // 💊 新增：资源收集动画系统
  handleResourceCollection(agentId: string, resourceType: 'MEDICAL' | 'WEAPON'): Promise<void> {
    const sprite = this.agents.get(agentId);
    if (!sprite) {
      return Promise.reject(new Error(`Agent not found: ${agentId}`));
    }

    switch (resourceType) {
      case 'MEDICAL':
        // 医疗资源：无动画，直接收集
        console.log(`${agentId} collected medical resource - no animation`);
        return Promise.resolve();
        
      case 'WEAPON':
        // 武器资源：播放atk动画一次，然后回到idle
        return new Promise<void>((resolve) => {
          const atkAnimKey = `${sprite.agentData.color}-atk`;
          sprite.play(atkAnimKey);
          
          console.log(`${agentId} collected weapon resource - playing atk animation`);
          
          // 等待atk动画完成 (8帧 * (1000/15fps) = ~533ms)
          setTimeout(() => {
            const idleAnimKey = `${sprite.agentData.color}-idle`;
            sprite.play(idleAnimKey);
            resolve();
          }, 600);
        });
        
      default:
        console.warn(`Unknown resource type: ${resourceType}`);
        return Promise.resolve();
    }
  }

  // State management
  updateAgentHealth(agentId: string, health: number): void {
    const sprite = this.agents.get(agentId);
    if (sprite) {
      sprite.agentData.health = health;
    }
  }

  setAgentAnimation(agentId: string, animation: string): void {
    const sprite = this.agents.get(agentId);
    if (sprite) {
      const animKey = `${sprite.agentData.color}-${animation}`;
      sprite.play(animKey);
    }
  }

  // WebSocket event handlers
  handleAgentSpawned(eventData: { agent: AgentData }): void {
    this.spawnAgent(eventData.agent);
  }

  handleAgentMoved(eventData: { agentId: string; fromPosition: { x: number; y: number }; toPosition: { x: number; y: number } }): void {
    // 🎮 使用带动画的tile跳跃移动替代瞬间传送
    const fromTile = { x: Math.floor(eventData.fromPosition.x / 24), y: Math.floor(eventData.fromPosition.y / 24) };
    const toTile = { x: Math.floor(eventData.toPosition.x / 24), y: Math.floor(eventData.toPosition.y / 24) };
    
    this.moveAgentToTile(eventData.agentId, fromTile, toTile).catch(error => {
      console.warn(`Agent move animation failed: ${error.message}`);
      // 失败时回退到直接更新位置
      this.updateAgentPosition(eventData.agentId, eventData.toPosition);
    });
  }

  // System cleanup
  cleanup(): void {
    this.agents.forEach((sprite, agentId) => {
      this.removeAgent(agentId);
    });
    this.agents.clear();
  }
}
